/** The AI writing coach — the writing_prompt player surface.
 *
 * Editor with a German-character toolbar (viobean-style umlaut buttons for
 * learners on non-German keyboards), live word count, and the graded
 * feedback panel: overall estimate, per-criterion scores, task-point
 * coverage, corrections, strengths/improvements. The server answers 503
 * while the coach is unconfigured — the UI degrades to a friendly notice.
 */
import { useEffect, useRef, useState } from "react";
import { ApiError } from "../../api/http";
import * as api from "../../api/exams";
import { Badge, Button, Spinner } from "../../components/ui";
import { TapToTranslate } from "../../components/TapToTranslate";
import { useT } from "../../i18n";

const GERMAN_CHARS = ["ä", "ö", "ü", "ß", "Ä", "Ö", "Ü"];

export function countWords(text: string): number {
  const trimmed = text.trim();
  return trimmed ? trimmed.split(/\s+/).length : 0;
}

function TaskPrompt({ payload }: { payload: Record<string, unknown> }) {
  return (
    <div className="space-y-3">
      {typeof payload.source_text === "string" && payload.source_text && (
        <div className="rounded-control bg-canvas p-3 text-sm italic text-ink">
          <TapToTranslate block text={payload.source_text} />
        </div>
      )}
      <ul className="list-disc pl-5 text-sm text-ink">
        {((payload.points as string[]) ?? []).map((point) => (
          <li key={point}>
            <TapToTranslate text={point} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function CriterionBar({ label, score }: { label: string; score: number }) {
  return (
    <div>
      <div className="flex justify-between text-sm">
        <span className="font-semibold text-ink">{label}</span>
        <span className="text-muted">{score} / 5</span>
      </div>
      <div className="mt-1 h-2 rounded-full bg-canvas">
        <div
          className="h-2 rounded-full bg-brand"
          style={{ width: `${(score / 5) * 100}%` }}
          aria-label={`${label} score ${score} of 5`}
        />
      </div>
    </div>
  );
}

function FeedbackPanel({
  submission,
  quota,
  onRevise,
}: {
  submission: api.WritingSubmission;
  quota: api.WritingQuota | null;
  onRevise: () => void;
}) {
  const t = useT();
  const fb = submission.feedback;
  return (
    <div className="space-y-5">
      <div className="text-center">
        <p className="text-4xl" aria-hidden>{fb.score_percent >= 60 ? "🎉" : "💪"}</p>
        <h2 className="mt-2 text-2xl font-extrabold">{t("writing.feedback")}</h2>
        <p className="mt-1 text-lg">
          <strong data-testid="writing-score">{fb.score_percent}%</strong>
          {" · "}
          {t("writing.levelEstimate")}: <strong>{fb.level_estimate}</strong>
        </p>
      </div>

      <div className="space-y-3">
        <CriterionBar label={t("writing.content")} score={fb.content_score} />
        <CriterionBar label={t("writing.structure")} score={fb.organisation_score} />
        <CriterionBar label={t("writing.language")} score={fb.language_score} />
      </div>

      {fb.task_points.length > 0 && (
        <div>
          <h3 className="font-bold text-ink">{t("writing.taskPoints")}</h3>
          <ul className="mt-2 space-y-1">
            {fb.task_points.map((tp) => (
              <li
                key={tp.point}
                className={`rounded-control border p-2 text-sm ${tp.addressed ? "border-success/40" : "border-danger/40"}`}
              >
                {tp.addressed ? "✓" : "✗"} <strong>{tp.point}</strong>
                {tp.comment && <span className="text-muted"> — {tp.comment}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {fb.corrections.length > 0 && (
        <div>
          <h3 className="font-bold text-ink">{t("writing.corrections")}</h3>
          <ul className="mt-2 space-y-2">
            {fb.corrections.map((c, i) => (
              <li key={i} className="rounded-control bg-canvas p-2 text-sm">
                <span className="text-danger line-through">{c.original}</span>{" "}
                → <strong className="text-success">{c.corrected}</strong>
                <p className="mt-0.5 text-muted">{c.explanation}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {fb.strengths.length > 0 && (
          <div>
            <h3 className="font-bold text-ink">{t("writing.strengths")}</h3>
            <ul className="mt-1 list-disc pl-5 text-sm text-ink">
              {fb.strengths.map((s) => <li key={s}>{s}</li>)}
            </ul>
          </div>
        )}
        {fb.improvements.length > 0 && (
          <div>
            <h3 className="font-bold text-ink">{t("writing.improvements")}</h3>
            <ul className="mt-1 list-disc pl-5 text-sm text-ink">
              {fb.improvements.map((s) => <li key={s}>{s}</li>)}
            </ul>
          </div>
        )}
      </div>

      {fb.overall_comment && (
        <p className="rounded-control bg-brand-soft p-3 text-sm text-brand-dark">
          {fb.overall_comment}
        </p>
      )}

      <div className="flex items-center justify-between">
        <Button variant="secondary" onClick={onRevise}>
          {t("writing.revise")}
        </Button>
        {quota && (
          <span className="text-xs text-muted">
            {t("writing.quotaUsed", { used: quota.used, limit: quota.limit })}
          </span>
        )}
      </div>
    </div>
  );
}

export function WritingCoach({ itemId, payload }: { itemId: string; payload: Record<string, unknown> }) {
  const t = useT();
  const [history, setHistory] = useState<api.WritingHistory | null>(null);
  const [text, setText] = useState("");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<api.WritingSubmission | null>(null);
  const areaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .writingHistory(itemId)
      .then((h) => {
        if (!cancelled) setHistory(h);
      })
      .catch(() => {
        if (!cancelled) setHistory({ configured: false, quota: null, submissions: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  if (!history) {
    return (
      <div className="grid place-items-center py-8">
        <Spinner size={24} />
      </div>
    );
  }

  if (!history.configured) {
    return (
      <div className="space-y-3">
        <TaskPrompt payload={payload} />
        <p role="status" className="rounded-control bg-canvas p-3 text-sm text-muted">
          {t("writing.notConfigured")}
        </p>
      </div>
    );
  }

  const insertChar = (ch: string) => {
    const el = areaRef.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? start;
    setText(text.slice(0, start) + ch + text.slice(end));
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + 1, start + 1);
    });
  };

  const onSubmit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.submitWriting(itemId, text);
      setView(result);
      setHistory((h) =>
        h ? { ...h, quota: result.quota, submissions: [result, ...h.submissions] } : h,
      );
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "error");
    } finally {
      setBusy(false);
    }
  };

  if (view) {
    return (
      <FeedbackPanel
        submission={view}
        quota={history.quota}
        onRevise={() => setView(null)}
      />
    );
  }

  return (
    <div className="space-y-4">
      <TaskPrompt payload={payload} />

      <div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label htmlFor="writing-text" className="text-sm font-semibold text-ink">
            {t("writing.yourText")}
          </label>
          <div className="flex flex-wrap gap-1">
            {GERMAN_CHARS.map((ch) => (
              <button
                key={ch}
                type="button"
                aria-label={`insert ${ch}`}
                onClick={() => insertChar(ch)}
                className="rounded-control border border-line px-2 py-0.5 text-sm text-ink hover:bg-canvas"
              >
                {ch}
              </button>
            ))}
          </div>
        </div>
        <textarea
          id="writing-text"
          ref={areaRef}
          rows={10}
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="mt-2 w-full rounded-control border border-line bg-surface p-3 text-sm text-ink"
        />
        <p className="mt-1 text-xs text-muted">
          {countWords(text)} {t("writing.words")}
        </p>
      </div>

      <div>
        <label htmlFor="writing-draft" className="text-sm font-semibold text-ink">
          {t("writing.draftNote")}
        </label>{" "}
        <span className="text-xs text-muted">{t("writing.draftNoteHint")}</span>
        <textarea
          id="writing-draft"
          rows={3}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="mt-1 w-full rounded-control border border-dashed border-line bg-canvas p-3 text-sm text-ink"
        />
      </div>

      {error && (
        <p role="alert" className="text-sm text-danger">{error}</p>
      )}

      <div className="flex items-center justify-between">
        <div className="text-xs text-muted">
          {history.quota && t("writing.quotaUsed", {
            used: history.quota.used,
            limit: history.quota.limit,
          })}
        </div>
        <Button onClick={onSubmit} loading={busy} disabled={countWords(text) === 0}>
          {t("writing.getFeedback")}
        </Button>
      </div>

      {history.submissions.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-ink">{t("writing.previous")}</h3>
          <ul className="mt-1 space-y-1">
            {history.submissions.map((s, i) => (
              <li key={s.id}>
                <button
                  type="button"
                  aria-label={`view feedback ${i + 1}`}
                  onClick={() => setView(s)}
                  className="text-sm text-brand hover:underline"
                >
                  {s.score_percent}% · {s.word_count} {t("writing.words")}
                  {s.created_at ? ` · ${new Date(s.created_at).toLocaleDateString()}` : ""}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Badge tone="brand">{t("writing.aiCoach")}</Badge>
    </div>
  );
}
